import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Chip, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { fmtCurrencyCents, fmtDate, fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { CashDeskShift } from '../../api/types';
import { useCashDeskShifts, useSitesList } from '../../api/queries';
import { SitePicker } from './SitePicker';
import { ShiftDetailDialog, varianceColor } from './ShiftDetailDialog';

/**
 * Cash Desk Activities (plan Draft 1 W3) — the shift ledger by site: every
 * shift with status, opening/closing balances, expected cash and variance,
 * drilling into that shift's transactions. Live as of W10a — fed by the
 * SC desk-journal uplink (cashdesk.shift projection + desk_transaction).
 */
export default function CashDeskActivitiesPage() {
  usePageTitle('Cash desk activities');
  const [siteId, setSiteId] = useState<string | null>(null);
  const sites = useSitesList();
  const sitesQ = useQuery({ queryKey: sites.queryKey, queryFn: sites.listFn });
  const siteLabel = sitesQ.data?.find((s) => s.id === siteId)?.displayName;
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Cash desk activities</Typography>
      <SitePicker siteId={siteId} onChange={setSiteId} />
      {siteId && <CashDeskActivitiesPanel siteId={siteId} siteLabel={siteLabel} />}
    </Stack>
  );
}

/** Site-scoped shift ledger — embedded by the Sites page's tabbed detail
 *  view and wrapped with a picker by the standalone page above. */
export function CashDeskActivitiesPanel({ siteId, siteLabel }: { siteId: string; siteLabel?: string }) {
  const { queryKey, listFn } = useCashDeskShifts(siteId);
  const [detailShift, setDetailShift] = useState<CashDeskShift | null>(null);

  const columns = useMemo<GridColDef<CashDeskShift>[]>(() => [
    { field: 'shiftNo', headerName: 'Shift', flex: 1.1,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {p.row.shiftNo !== null ? `#${p.row.shiftNo} · ` : ''}{p.row.terminalName ?? p.row.deskCode}
          </Typography>
          <Typography variant="caption" color="text.secondary">{p.row.cashierName}</Typography>
        </Stack>
      ) },
    { field: 'status', headerName: 'Status', width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.row.status}
              color={p.row.status === 'open' ? 'success' : 'default'} />
      ) },
    { field: 'gamingDay', headerName: 'Gaming day', width: 130,
      valueFormatter: (value) => fmtDate(value) },
    { field: 'openingCents', headerName: 'Opening', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (value) => fmtCurrencyCents(value) },
    { field: 'expectedCents', headerName: 'Expected', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (value) => (value !== null && value !== undefined ? fmtCurrencyCents(value) : '—') },
    { field: 'closingCents', headerName: 'Counted', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      valueFormatter: (value) => (value !== null && value !== undefined ? fmtCurrencyCents(value) : '—') },
    { field: 'varianceCents', headerName: 'Variance', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    color={varianceColor(p.row.varianceCents)}>
          {p.row.varianceCents !== null ? fmtCurrencyCents(p.row.varianceCents) : '—'}
        </Typography>
      ) },
    { field: 'transferCount', headerName: 'Transfers', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {p.row.transferCount} · {fmtCurrencyCents(p.row.transferTotalCents)}
        </Typography>
      ) },
    { field: 'payoutCount', headerName: 'Payouts', width: 110,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {p.row.payoutCount} · {fmtCurrencyCents(p.row.payoutTotalCents)}
        </Typography>
      ) },
    { field: 'openedAt', headerName: 'Opened', width: 170,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'cashierName', label: 'Cashier' },
    { kind: 'text',   key: 'deskCode',    label: 'Desk' },
    { kind: 'select', key: 'status',      label: 'Status', options: [
      { value: 'open',   label: 'Open' },
      { value: 'closed', label: 'Closed' },
    ] },
    { kind: 'text', key: 'gamingDay', label: 'Gaming day (YYYY-MM-DD)' },
  ], []);

  const quickFilters = useMemo<QuickFilter<CashDeskShift>[]>(() => [
    { label: 'All',    filter: () => true },
    { label: 'Open',   filter: (r) => r.status === 'open' },
    { label: 'Closed', filter: (r) => r.status === 'closed' },
  ], []);

  const exportColumns = useMemo<ExportColumn<CashDeskShift>[]>(() => [
    { key: 'shiftNo',            label: 'Shift No', format: (v) => (v !== null ? String(v) : '—') },
    { key: 'deskCode',           label: 'Desk' },
    { key: 'cashierName',        label: 'Cashier' },
    { key: 'terminalName',       label: 'Terminal', format: (v) => fmtOrEmDash(v) },
    { key: 'status',             label: 'Status' },
    { key: 'gamingDay',          label: 'Gaming Day', format: (v) => fmtDate(v) },
    { key: 'openingCents',       label: 'Opening',  format: (v) => fmtCurrencyCents(v) },
    { key: 'expectedCents',      label: 'Expected', format: (v) => (v !== null ? fmtCurrencyCents(v) : '—') },
    { key: 'closingCents',       label: 'Counted',  format: (v) => (v !== null ? fmtCurrencyCents(v) : '—') },
    { key: 'varianceCents',      label: 'Variance', format: (v) => (v !== null ? fmtCurrencyCents(v) : '—') },
    { key: 'transferCount',      label: 'Transfers' },
    { key: 'transferTotalCents', label: 'Transfer Total', format: (v) => fmtCurrencyCents(v) },
    { key: 'payoutCount',        label: 'Payouts' },
    { key: 'payoutTotalCents',   label: 'Payout Total', format: (v) => fmtCurrencyCents(v) },
    { key: 'openedAt',           label: 'Opened', format: (v) => fmtDateTime(v) },
    { key: 'closedAt',           label: 'Closed', format: (v) => (v ? fmtDateTime(v) : '—') },
  ], []);

  const actions = useMemo<RowAction<CashDeskShift>[]>(() => [
    { label: 'View', onClick: (row) => setDetailShift(row) },
  ], []);

  return (
    <Stack spacing={2}>
      <RouteDataGrid<CashDeskShift & { id: string }>
        entityName="Cash desk shifts"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Cash desk activities"
        actions={actions}
        onRowClick={(row) => setDetailShift(row)}
        emptyMessage="No cash desk shifts recorded for this site yet."
      />

      <ShiftDetailDialog shift={detailShift} siteLabel={siteLabel} onClose={() => setDetailShift(null)} />
    </Stack>
  );
}
